import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';

export function MarginCalculator() {
  const [cost, setCost] = useState(500);
  const [marginPct, setMarginPct] = useState(40);

  const result = useMemo(() => {
    const price = cost / (1 - marginPct / 100);
    const profit = price - cost;
    const markup = (profit / cost) * 100;
    return { price, profit, markup };
  }, [cost, marginPct]);

  return (
    <div>
      <ToolHeader
        toolId="margin-calculator"
        title="Margin & markup calculator"
        description="Compute selling price, margin, and markup from cost. Or input price to derive margin."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="space-y-3">
          <Field label="Cost (₹)">
            <Input
              type="number"
              value={cost}
              onChange={(e) => setCost(Math.max(0, Number(e.target.value) || 0))}
              className="font-mono"
            />
          </Field>
          <Field label="Margin (%)">
            <Input
              type="number"
              value={marginPct}
              onChange={(e) => setMarginPct(Math.max(0, Math.min(99, Number(e.target.value) || 0)))}
              className="font-mono"
            />
          </Field>
        </Card>
        <Card>
          <Stat label="Selling price" value={result.price.toFixed(2)} prefix="₹" big />
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Stat label="Profit" value={result.profit.toFixed(2)} prefix="₹" />
            <Stat label="Markup" value={result.markup.toFixed(2)} suffix="%" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  prefix,
  suffix,
  big,
}: {
  label: string;
  value: string;
  prefix?: string;
  suffix?: string;
  big?: boolean;
}) {
  return (
    <div className={big ? '' : 'p-3 rounded-lg bg-bg-inset border border-border'}>
      <div className="text-2xs uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className={`font-mono font-semibold tabular-nums mt-1 ${big ? 'text-4xl' : 'text-sm'}`}>
        {prefix}
        {value}
        {suffix}
      </div>
    </div>
  );
}

export default MarginCalculator;
